import React, { createContext, useContext, useMemo, useState } from 'react';

type ToastType = 'success' | 'error' | 'info';

export interface ToastOptions {
  type?: ToastType;
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number; // default 5000
}

interface ToastItem extends Required<ToastOptions> {
  id: string;
  message: string;
}

interface ToastContextValue {
  show: (message: string, options?: ToastOptions) => void;
  success: (message: string, options?: Omit<ToastOptions, 'type'>) => void;
  error: (message: string, options?: Omit<ToastOptions, 'type'>) => void;
  info: (message: string, options?: Omit<ToastOptions, 'type'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  const show = (message: string, options?: ToastOptions) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: ToastItem = {
      id,
      message,
      type: options?.type ?? 'info',
      actionLabel: options?.actionLabel ?? '',
      onAction: options?.onAction ?? (() => {}),
      durationMs: options?.durationMs ?? 5000,
    };
    setToasts(prev => [...prev, toast]);
    if (toast.durationMs > 0) {
      setTimeout(() => dismiss(id), toast.durationMs);
    }
  };

  const value = useMemo<ToastContextValue>(() => ({
    show,
    success: (message, options) => show(message, { ...options, type: 'success' }),
    error: (message, options) => show(message, { ...options, type: 'error' }),
    info: (message, options) => show(message, { ...options, type: 'info' }),
    dismiss,
  }), []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Renderer */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`max-w-sm w-[360px] rounded-lg shadow-lg border px-4 py-3 bg-white ${
              t.type === 'success' ? 'border-green-200' : t.type === 'error' ? 'border-red-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`h-2.5 w-2.5 mt-2 rounded-full ${
                  t.type === 'success' ? 'bg-green-500' : t.type === 'error' ? 'bg-red-500' : 'bg-gray-400'
                }`}
              />
              <div className="flex-1">
                <div className="text-sm text-gray-900">{t.message}</div>
                {t.actionLabel && (
                  <button
                    onClick={() => {
                      try { t.onAction?.(); } finally { dismiss(t.id); }
                    }}
                    className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {t.actionLabel}
                  </button>
                )}
              </div>
              <button
                aria-label="Fechar"
                onClick={() => dismiss(t.id)}
                className="ml-2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
